import { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'es';

interface Translations {
  overview: string;
  understand: string;
  implications: string;
  decide: string;
  research: string;
  client: string;
  market: string;
  pricing: string;
  launch: string;
  portfolio: string;
  marketSnapshot: string;
  yourPosition: string;
  marketSize: string;
  growthRate: string;
  yourShare: string;
  marketRank: string;
  loading: string;
  error: string;
  retry: string;
  selectClient: string;
  diagnosePerformance: string;
  selectWindowToAnalyze: string;
  clickWindowAnalyze: string;
  analysisComingSoon: string;
  // Client Dashboard
  enabledModules: string;
  topBrands: string;
  marketValue: string;
  skusActive: string;
  yoyGrowth: string;
  noModulesConfigured: string;
  noBrandData: string;
  advanced: string;
  basic: string;
  simulations: string;
}

const translations: Record<Language, Translations> = {
  en: {
    overview: 'Overview',
    understand: 'Understand',
    implications: 'Implications',
    decide: 'Decide',
    research: 'Research',
    client: 'Client',
    market: 'Market',
    pricing: 'Pricing',
    launch: 'Launch',
    portfolio: 'Portfolio',
    marketSnapshot: 'Market Snapshot',
    yourPosition: 'Your Position',
    marketSize: 'Market Size',
    growthRate: 'Growth Rate',
    yourShare: 'Your Share',
    marketRank: 'Market Rank',
    loading: 'Loading...',
    error: 'An error occurred',
    retry: 'Retry',
    selectClient: 'Select Client',
    diagnosePerformance: 'Diagnose performance and identify opportunities',
    selectWindowToAnalyze: 'Select a window to start analyzing',
    clickWindowAnalyze: 'Click any window above to open its analysis panel',
    analysisComingSoon: 'Analysis coming soon',
    // Client Dashboard
    enabledModules: 'Enabled Modules',
    topBrands: 'Top Brands',
    marketValue: 'Market Value',
    skusActive: 'SKUs Active',
    yoyGrowth: 'YoY Growth',
    noModulesConfigured: 'No modules configured',
    noBrandData: 'No brand data available',
    advanced: 'advanced',
    basic: 'basic',
    simulations: 'Simulations',
  },
  es: {
    overview: 'Resumen',
    understand: 'Entender',
    implications: 'Implicaciones',
    decide: 'Decidir',
    research: 'Investigar',
    client: 'Cliente',
    market: 'Mercado',
    pricing: 'Precios',
    launch: 'Lanzamiento',
    portfolio: 'Portafolio',
    marketSnapshot: 'Vista del Mercado',
    yourPosition: 'Tu Posicion',
    marketSize: 'Tamano del Mercado',
    growthRate: 'Tasa de Crecimiento',
    yourShare: 'Tu Participacion',
    marketRank: 'Ranking de Mercado',
    loading: 'Cargando...',
    error: 'Ocurrio un error',
    retry: 'Reintentar',
    selectClient: 'Seleccionar Cliente',
    diagnosePerformance: 'Diagnosticar rendimiento e identificar oportunidades',
    selectWindowToAnalyze: 'Selecciona una ventana para analizar',
    clickWindowAnalyze: 'Haz clic en cualquier ventana para abrir su panel de analisis',
    analysisComingSoon: 'Analisis proximamente',
    // Client Dashboard
    enabledModules: 'Modulos Habilitados',
    topBrands: 'Marcas Principales',
    marketValue: 'Valor del Mercado',
    skusActive: 'SKUs Activos',
    yoyGrowth: 'Crecimiento Anual',
    noModulesConfigured: 'Sin modulos configurados',
    noBrandData: 'Sin datos de marcas',
    advanced: 'avanzado',
    basic: 'basico',
    simulations: 'Simulaciones',
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('en');

  const value = {
    language,
    setLanguage,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
